import { ICompileService, IPromise, IQService, Injectable } from "angular";
import { ColDef } from "../../../records/grids/columns/colDef";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import { RecordDataSource } from "../../../records/grids";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";

import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";

import { machineConfigurationObjectName } from "../../shared/names";
import { moneyValueGetterFactory } from "../../../records/grids/cells/valueGetters";
export { machineConfigurationObjectName as vsObjectName } from "../../shared/names";

import { vsObjectName as typeIdName } from "./machineTypeId";

export const machineConfigViewDefaultSort =
  "PropertyName,Status,Location,SerialNumber,-Date,Order";

machineConfigurationConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];

export function machineConfigurationConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(
    machineConfigurationObjectName,
    [
      ServerUtilityName,
      GridSettingsServiceName,
      (
        serverUtility: ServerUtility,
        gridSettingsService: GridSettingsService
      ) => {
        return {
          columnDefinitionSet: machineConfigurationObjectName,
          defaultSort: machineConfigViewDefaultSort,
          gridDataSource: new RecordDataSource(
            serverUtility,
            "/api/machines/MachineConfiguration"
          ),
          gridSettings: gridSettingsService.getGridSettings(
            Symbol(machineConfigurationObjectName)
          ),
          nameSingular: "Configuration",
          namePlural: "Configurations",
          showAddButton: false,
          showAddButtonPermission: "machineEdit",
        };
      },
    ]
  );

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(
    machineConfigurationObjectName,
    [
      ColumnDefinitionServiceName,
      ColumnDefinitionSetServiceName,
      "$compile",
      (
        columnDefinitionService: ColumnDefinitionService,
        columnDefinitionSetService: ColumnDefinitionSetService,
        $compile: ICompileService
      ) => {
        const typeIdColumns =
          columnDefinitionSetService.getColumnDefinitionSet(typeIdName);

        const excludeTypeIdColumns = ["Name", "Description"];

        const columns = [
          columnDefinitionService.getNumberColDef({
            field: "Id",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "MachineId",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "MachineConfigurationId",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "PropertyName",
          }),
          columnDefinitionService.getDateColDef({
            field: "Date",
          }),
          columnDefinitionService.getStringColDef({
            field: "Location",
          }),
          columnDefinitionService.getStringColDef({
            field: "SerialNumber",
            header: "Serial Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "Notes",
          }),
          columnDefinitionService.getStringColDef({
            field: "SlotNumber",
            header: "Asset Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "Status",
            filter: new FilterProvider("Status", $compile)
              .addFilter(
                "=",
                new OptionsFilter({
                  displayName: "=",
                  queryFieldNames: ["Status"],
                  options: [
                    {
                      display: "Floor",
                      value: "Floor",
                    },
                    {
                      display: "Warehoused",
                      value: "Warehoused",
                    },
                    {
                      display: "Decommissioned",
                      value: "Decommissioned",
                    },
                  ],
                })
              )
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineTypeIdName",
            header: "Type Id",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineTypeIdDescription",
            header: "Type Id Description",
          }),
          columnDefinitionService.getDateColDef({
            field: "InServiceDate",
            header: "In Service Date",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineAreaName",
            header: "Area",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Section",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Bank",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Position",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "WarehouseLocation",
            header: "Warehouse Location",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineBankConfigName",
            header: "Bank Config",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "TgraGovStamp",
            header: "TGRA # - Gov Stamp",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CameraCoverage",
            header: "Camera Coverage",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "ProgressiveController",
            header: "Progressive Controller",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ControllerLocation",
            header: "Controller Location",
          }),
          columnDefinitionService.getStringColDef({
            field: "ProgressiveSerialNumber",
            header: "Progressive Serial Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "ControllerType",
            header: "Controller Type",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "Leased",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "LeasedSoftware",
            header: "Leased Software",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "SpecialtyPupd",
            header: "Specialty PUPD",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineLeaseFeeName",
            header: "Lease Fee",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineLeaseTypeName",
            header: "Lease Type",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "CreditLimit",
            header: "Credit Limit",
            valueGetter: moneyValueGetterFactory(),
          }),
          columnDefinitionService.getNumberColDef({
            field: "JackpotLimit",
            header: "Jackpot Limit",
            valueGetter: moneyValueGetterFactory(),
          }),
          columnDefinitionService.getStringColDef({
            field: "Printer",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "BvGlCode",
            header: "BV-G/L Code",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineCabinetTypeName",
            header: "Cabinet Type",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineModelName",
            header: "Model",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "NumberOfReels",
            header: "Number of Reels",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineManufacturerName",
            header: "Manufacturer",
          }),
          columnDefinitionService.getDateColDef({
            field: "PurchaseDate",
            header: "Purchase Date",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CurrentConfiguration",
            header: "Current Configuration",
          }),
          columnDefinitionService.getStringColDef({
            field: "ChangeType",
            filter: new FilterProvider("ChangeType", $compile)
              .addFilter(
                "=",
                new OptionsFilter({
                  displayName: "=",
                  queryFieldNames: ["ChangeType"],
                  options: [
                    {
                      display: "Conversion",
                      value: "Conversion",
                    },
                    {
                      display: "Decommission",
                      value: "Decommission",
                    },
                    {
                      display: "Move",
                      value: "Move",
                    },
                    {
                      display: "New Machine",
                      value: "NewMachine",
                    },
                    {
                      display: "Warehouse",
                      value: "Warehouse",
                    },
                  ],
                }),
              )
              .getGridFilterFactory(),
          }),
          ...typeIdColumns.filter(
            (c) => !excludeTypeIdColumns.includes(c.field!)
          ),
        ];

        return columns;
      },
    ]
  );
}
